import { useQuery } from "urql"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { JsonViewer } from "@/components/shared/json-viewer"
import { DataTable, type Column } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { EventQuery } from "@/lib/graphql/queries"
import type { ResultOf } from "@/lib/graphql/types"

type EventDelivery = NonNullable<ResultOf<typeof EventQuery>["event"]>["deliveries"][number]

const deliveryColumns: Column<EventDelivery>[] = [
  {
    header: "Destination",
    cell: (row) => <span className="text-xs">{row.destination.name}</span>,
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Attempts",
    cell: (row) => (
      <span className="text-xs text-muted-foreground">
        {row.attempts}/{row.maxAttempts}
      </span>
    ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

export function ShareEvent({ id }: { id: string }) {
  const [result] = useQuery({ query: EventQuery, variables: { id } })

  const event = result.data?.event

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!event) {
    return <EmptyState title="Event not found" />
  }

  const body = event.rawBody ? atob(event.rawBody) : null
  let parsedBody: unknown = body
  try {
    if (body) parsedBody = JSON.parse(body)
  } catch {
    // keep as string
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">{event.eventType}</h1>
        <StatusBadge status={event.status} />
      </div>

      <Card className="border-border/30">
        <CardHeader>
          <CardTitle className={sectionTitleClass}>Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel className="mb-1">Source</FieldLabel>
              <div>{event.source.name}</div>
            </div>
            <div>
              <FieldLabel className="mb-1">Received</FieldLabel>
              <TimeAgo date={event.receivedAt} />
            </div>
            <div>
              <FieldLabel className="mb-1">Idempotency Key</FieldLabel>
              <code className="text-xs font-mono">{event.idempotencyKey}</code>
            </div>
          </div>
        </CardContent>
      </Card>

      {event.headers && (
        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Headers</CardTitle>
          </CardHeader>
          <CardContent>
            <JsonViewer data={event.headers} />
          </CardContent>
        </Card>
      )}

      {parsedBody != null && (
        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Body</CardTitle>
          </CardHeader>
          <CardContent>
            {typeof parsedBody === "object" ? (
              <JsonViewer data={parsedBody as Record<string, unknown>} />
            ) : (
              <pre className="text-xs font-mono whitespace-pre-wrap break-all">{String(parsedBody as string)}</pre>
            )}
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        <h2 className="text-sm font-medium">Deliveries</h2>
        <DataTable
          columns={deliveryColumns}
          data={event.deliveries}
          keyFn={(row) => row.id}
          emptyTitle="No deliveries"
        />
      </div>
    </div>
  )
}
